import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { CommonModule } from '@angular/common';
import { PasswordService } from '../../services/password/password.service';
import { PasswordResetRecord, ResetPasswordRequest } from '../../models/reset-password.model';
import { confirmPasswordMatchValidator, customPasswordValidator } from '../../shared/utils/validators';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressBarModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  /** Token provided as path variable */
  @Input() token!: string;

  /** Associated reset record with provided token */
  resetRecord: PasswordResetRecord | null = null;

  /** Form for resetting password */
  resetPasswordForm!: FormGroup;

  /** If component is loading token */
  isLoading = false;

  /** If component is submitting */
  isSubmitting = false;

  /** Error/Success messages */
  errorMessage = '';
  successMessage = '';

  /** Flags for hiding and showing password fields */
  hidePassword = true;

  /**
   * Constructor to inject dependencies and create the form
   * 
   * @param passwordService 
   * @param router 
   * @param fb 
   */
  constructor(
    private passwordService: PasswordService,
    private router: Router,
    private fb: FormBuilder,
  ) {
    this.resetPasswordForm = this.fb.group({
      password: ['', customPasswordValidator],
      confirmPassword: ['', [Validators.required, confirmPasswordMatchValidator]],
    });
  }

  /**
   * On startup, validate the token
   */
  ngOnInit(): void {
    // Check if token is provided
    if (!this.token) {
      this.errorMessage = 'Invalid reset link. Please request a new password reset.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    // Validate the reset token
    this.passwordService.fetchResetToken(this.token).subscribe({
      next: (record) => {
        // On successful fetch, check validity set the reset record
        this.isLoading = false;

        // Check if record has been used
        if (record.used) {
          this.errorMessage = 'This reset link has already been used. Please request a new password reset.';
          return;
        }

        // Check if record has expired
        const expirationDate = new Date(record.expiresAt);
        const now = new Date();

        if (expirationDate < now) {
          this.errorMessage = 'This reset link has expired. Please request a new password reset.';
          return;
        }

        this.resetRecord = record;
      },
      error: (error) => {
        // On error, set loading to false and show error message
        this.isLoading = false;
        this.errorMessage = 'Invalid or expired reset link. Please request a new password reset.';
        console.error('Token validation error:', error);
      },
    });
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.resetPasswordForm.invalid || !this.resetRecord) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    const token = this.token;
    const newPassword = this.resetPasswordForm.get('password')?.value;

    this.passwordService.resetPassword(token, newPassword).subscribe({
      next: () => {
        // On success, reset form and show success message
        this.isSubmitting = false;
        this.successMessage = 'Your password has been successfully reset. You can now log in with your new password.';
        this.resetPasswordForm.reset();

        // Redirect to login after 3 seconds
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 3000);
      },
      error: (error) => {
        // On error, set submitting to false and show error message
        this.isSubmitting = false;
        this.errorMessage = error.error?.message || 'An error occurred while resetting your password. Please try again.';
        console.error('Reset password error:', error);
      },
    });
  }

  /**
   * Navigate back to login
   */
  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
